import { Input, Typography, CheckIcon, CloseAltIcon } from '@mgmresorts/wcl-lab';
import React, { useCallback, useMemo } from 'react';

import { useContent } from '../../../../hooks/useContent';
import { BindInput } from '../../../../hooks/useInput';
import { MaxLengths } from '../../../../utils/constants';

import StyledContent from './StyledContent';

type PasswordInputProps = BindInput & {
  id: string;
  label: string;
  name: string;
  error?: boolean;
  hint?: string;
  disabled?: boolean;
  className?: string;
  passwordValidations: { [key: string]: boolean };
  testId?: string;
  invalidFields: string[];
};

export function PasswordInput(props: PasswordInputProps) {
  const { value, invalidFields, name, passwordValidations } = props;
  const { getContentByPath } = useContent();

  const isValidPassword = useMemo(
    () => Object.values(passwordValidations).every(validation => validation),
    [passwordValidations],
  );

  const getPasswordFieldError = useCallback(() => {
    if (invalidFields.includes(name)) {
      return getContentByPath('shared.fields.password.empty');
    }

    if (!isValidPassword) {
      return getContentByPath('shared.fields.password.policy');
    }

    if (!invalidFields.includes('password')) {
      return '';
    }

    return '';
  }, [invalidFields, isValidPassword, getContentByPath, name]);

  const error = useMemo(
    () => (invalidFields.includes('password') || (value && !isValidPassword) ? getPasswordFieldError() : undefined),
    [getPasswordFieldError, invalidFields, isValidPassword, value],
  );

  const passwordValidationHint = useCallback(
    (validation: keyof typeof passwordValidations, hint: string) => {
      const isValid = passwordValidations[validation];
      if (isValid) {
        return null;
      }
      const color = '#E02D21';
      const Icon = CloseAltIcon;
      return (
        <div className='identity__password-hint'>
          <Icon color={color} size={20} width={40} />
          <Typography variant='small' color={color}>
            {hint}
          </Typography>
        </div>
      );
    },
    [passwordValidations],
  );

  return (
    <StyledContent>
      <Input type='password' {...props} maxLength={MaxLengths.Password} />
      <div className='identity__password-hints-wrapper' data-testid='password-hints-wrapper'>
        <>
          {(value || error) && (
            <>
              {passwordValidationHint('length', getContentByPath('shared.fields.password.lengthHint'))}
              {passwordValidationHint('number', getContentByPath('shared.fields.password.numberHint'))}
              {passwordValidationHint('uppercase', getContentByPath('shared.fields.password.uppercaseHint'))}
              {passwordValidationHint('lowercase', getContentByPath('shared.fields.password.lowercaseHint'))}
              {passwordValidationHint(
                'specialCharacter',
                getContentByPath('shared.fields.password.specialCharacterHint'),
              )}
              {value.length > 0 &&
                passwordValidationHint('firstName', getContentByPath('shared.fields.password.firstName'))}
              {value.length > 0 &&
                passwordValidationHint('lastName', getContentByPath('shared.fields.password.lastName'))}
            </>
          )}
          {value && !error && (
            <div className='identity__password-hint'>
              <CheckIcon size={20} width={40} />
              <Typography variant='small'>{getContentByPath('shared.fields.password.allMet')}</Typography>
            </div>
          )}
        </>
      </div>
    </StyledContent>
  );
}

PasswordInput.displayName = 'PasswordInputField';
